import { ImageLoaderProps } from 'next/image';
import { twMerge } from 'tailwind-merge';

import { useAnalytics } from '../../../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../../../helpers/analytics/trackEvent';
import { getImgProxyLoader } from '../../../../helpers/imageLoaders/imgProxy';
import { getSpeciesGroupRegionUrl } from '../../../../helpers/urls';
import { EventMykissCardClicked } from '../../../../interfaces/events';
import { InternalLink } from '../../../Clickables/InternalLink/InternalLink';
import { ArrowRightIcon } from '../../../Icons/ArrowRightIcon';
import { FishCatchesIcon } from '../../../Icons/FishCatchesIcon';
import { NextImageWrapper } from '../../../NextImage/NextImageWrapper';

interface IProps {
  name: string;
  speciesId: string;
  /* An identifier for where this carousel is being used, e.g. species-top-waters */
  clickEventSource: EventMykissCardClicked['properties']['source'];
  /* An ID of the resource type where this carousel is being used, e.g. a species ID if it's on a species page */
  clickEventSourceId?: EventMykissCardClicked['properties']['source_id'];
  /* The position of the card in a list (if applicable) */
  clickEventPosition?: EventMykissCardClicked['properties']['position'];
  countryCode: string;
  regionCode: string;
  className?: string;
  imageClassName?: string;
  imageUrl?: string;
  imageWidth?: number;
  imageHeight?: number;
  useCustomLoader?: boolean;
}

export const SpeciesGroupCard = ({
  speciesId,
  name,
  clickEventSource,
  clickEventPosition,
  clickEventSourceId,
  className = '',
  countryCode,
  regionCode,
  imageClassName = '',
  imageUrl,
  imageWidth,
  imageHeight,
  useCustomLoader = true,
}: IProps) => {
  const { currentPage } = useAnalytics();
  const imgProxyLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getImgProxyLoader(src, width, imageWidth ?? 0, imageHeight, { quality });

  const handleSpeciesClick = () => {
    void trackEvent({
      name: 'mykiss_card_clicked',
      properties: {
        source: clickEventSource,
        target: 'species',
        target_id: speciesId,
        position: clickEventPosition,
        source_id: clickEventSourceId,
        page: currentPage,
      },
    });
  };

  return (
    <div
      className={twMerge(
        'border-b-twilight relative w-full border-b py-3 pr-2 md:py-2.5',
        className,
      )}
    >
      <div className="relative flex items-center gap-4">
        {imageUrl && imageWidth && imageHeight ? (
          <NextImageWrapper
            wrapperClassName="w-32 h-12 md:h-11 md:w-28"
            imageClassName={twMerge('size-full object-contain', imageClassName)}
            customPlaceholderIcon={
              <FishCatchesIcon className="size-full object-contain" size={93} />
            }
            customPlaceholderClassName="text-twilight p-0 bg-sea-foam sm:bg-lake-fog sm:text-twilight"
            alt={name}
            src={imageUrl}
            loader={useCustomLoader ? imgProxyLoader : undefined}
            fill
            sizes="(min-width: 990px) 128vw, 112px"
          />
        ) : (
          <div className="bg-twilight text-lake-fog mb-3 flex h-11 w-28 items-center justify-center rounded-md md:rounded-lg">
            <FishCatchesIcon size={93} />
          </div>
        )}
        <div className="flex flex-col gap-3 md:gap-2">
          <InternalLink
            to={getSpeciesGroupRegionUrl(countryCode, regionCode, speciesId)}
            variant="textNone"
            onClick={handleSpeciesClick}
            className="body-text-sm m-0 flex items-center gap-1 font-semibold"
            size="xs"
          >
            {name}
            <ArrowRightIcon width={16} height={16} />
          </InternalLink>
        </div>
      </div>
    </div>
  );
};
